/**
 * Support Ticket detail — shared by staff and installers.
 *
 * Installers can see their own ticket + open attachments. Staff can also
 * change status/priority via a small toolbar. Attachments are fetched
 * through the authenticated backend endpoint so object-storage URLs stay
 * private.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Home, Paperclip, Loader2, Flame, Zap, Clock, LifeBuoy, Trash2, StickyNote, Send, User, Pencil, Check, Phone, Mail, Truck, Hammer, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';
import { AttachmentGallery, AttachmentUploader, NoteImagePicker, uploadTicketFiles } from '@/components/support/TicketAttachments';
import { MentionInput, renderWithMentions } from '@/components/support/MentionInput';
import { NoteTemplateChips } from '@/components/support/TicketNotes';
import useHomePath from '@/hooks/useHomePath';
import { SupportContactModal } from '@/components/support/SupportContactModal';

const STATUS_OPTIONS = [
  { key: 'new',                label: 'New' },
  { key: 'awaiting_customer',  label: 'Awaiting Customer' },
  { key: 'awaiting_ox',        label: 'Awaiting Ox Action' },
  { key: 'complete',           label: 'Query Complete' },
];
const PRIORITY_OPTIONS = [
  { key: 'urgent',   label: 'Urgent',   icon: Flame, chip: 'bg-rose-100 text-rose-800' },
  { key: 'priority', label: 'Priority', icon: Zap,   chip: 'bg-amber-100 text-amber-800' },
  { key: 'standard', label: 'Standard', icon: Clock, chip: 'bg-slate-100 text-slate-700' },
];
const CATEGORY_OPTIONS = [
  { key: 'delivery',             label: 'Delivery',             icon: Truck },
  { key: 'installation_support', label: 'Installation Support', icon: Hammer },
  { key: 'training_course',      label: 'Training Course',      icon: GraduationCap },
];

export default function SupportTicketDetail() {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const { user } = useContext(AuthContext);
  const [contactChannel, setContactChannel] = useState(null);
  const isStaff = ['admin', 'manager', 'team'].includes(user?.role);
  const canAdmin = ['admin', 'manager'].includes(user?.role);
  const homePath = useHomePath();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [mentionIds, setMentionIds] = useState([]);
  const [addingNote, setAddingNote] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [titleEdit, setTitleEdit] = useState(null); // null = not editing, string = draft

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Load failed');
      setTicket(await res.json());
    } catch (e) {
      toast.error(e.message);
      navigate(isStaff ? '/staff/support' : '/support');
    } finally {
      setLoading(false);
    }
  }, [ticketId, navigate, isStaff]);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Roster for the assignee dropdown — staff only.
  useEffect(() => {
    if (!isStaff) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/support/staff-members`, { credentials: 'include' });
        if (res.ok) setAssignees(await res.json());
      } catch (err) { logger.warn('[support] failed to load staff-members roster:', err); }
    })();
  }, [isStaff]);

  const patch = async (body) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Update failed');
      setTicket(await res.json());
      toast.success('Request updated');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteTicket = async () => {
    if (!canAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      toast.success('Request deleted');
      navigate(isStaff ? '/staff/support' : '/support');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const [noteFiles, setNoteFiles] = useState([]);
  const addNote = async (e) => {
    e?.preventDefault?.();
    const body = noteDraft.trim();
    if ((!body && noteFiles.length === 0) || addingNote) return;
    setAddingNote(true);
    try {
      const uploaded = noteFiles.length ? await uploadTicketFiles(ticketId, noteFiles) : [];
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}/notes`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body, attachment_ids: uploaded.map((a) => a.id), mention_user_ids: mentionIds }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to add note');
      const note = await res.json();
      // Optimistic append — avoids a full reload just to see the note appear
      setTicket((t) => ({ ...t, notes: [...(t.notes || []), note], attachments: [...(t.attachments || []), ...uploaded] }));
      setNoteDraft('');
      setNoteFiles([]);
      if (note.mentions?.length) toast.success(`Pinged ${note.mentions.map((m) => m.name).join(', ')} by email`);
      setMentionIds([]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddingNote(false);
    }
  };

  const toggleDone = async (n) => {
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}/notes/${n.id}/done`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ done: !n.done_at }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Could not update note');
      const updated = await res.json();
      setTicket((t) => ({ ...t, notes: (t.notes || []).map((x) => (x.id === n.id ? updated : x)) }));
    } catch (err) { toast.error(err.message); }
  };

  const deleteNote = async (noteId) => {
    if (!canAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}/notes/${noteId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to delete note');
      setTicket((t) => ({ ...t, notes: (t.notes || []).filter((n) => n.id !== noteId) }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading || !ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  const pMeta = PRIORITY_OPTIONS.find((p) => p.key === ticket.priority) || PRIORITY_OPTIONS[2];
  const PIcon = pMeta.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0f172a, #334155)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(isStaff ? '/staff/support' : '/support')}
            data-testid="ticket-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-white/60 font-mono">{ticket.reference} · {ticket.installer_name}</div>
            {titleEdit !== null ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const t = titleEdit.trim();
                  if (t && t !== ticket.title) await patch({ title: t });
                  setTitleEdit(null);
                }}
                className="flex items-center gap-2 mt-0.5"
              >
                <Input
                  autoFocus
                  value={titleEdit}
                  onChange={(e) => setTitleEdit(e.target.value)}
                  className="bg-white text-slate-900 h-8 text-base sm:text-lg font-semibold"
                  maxLength={200}
                  data-testid="ticket-title-input"
                  onBlur={() => setTitleEdit(null)}
                />
                <Button type="submit" size="sm" className="bg-white text-slate-900 hover:bg-slate-100 shrink-0" data-testid="ticket-title-save">
                  <Check className="w-4 h-4" />
                </Button>
              </form>
            ) : (
              <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate group">
                <LifeBuoy className="w-5 h-5 shrink-0" />
                <span className="truncate" data-testid="ticket-title-display">{ticket.title || 'Support'}</span>
                {isStaff && (
                  <button
                    type="button"
                    onClick={() => setTitleEdit(ticket.title || 'Support')}
                    className="opacity-60 hover:opacity-100 shrink-0"
                    data-testid="ticket-title-edit"
                    aria-label="Rename ticket"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </h1>
            )}
          </div>
          {canAdmin && (
            <Button
              onClick={deleteTicket}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/30 text-white hover:bg-rose-600/90 hover:border-rose-500 shrink-0"
              data-testid="ticket-delete"
            >
              <Trash2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Delete</span>
            </Button>
          )}
          <Button
            onClick={() => navigate(homePath)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
            aria-label="Home"
          >
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Contact — surfaced up top so staff can call/email straight from the request */}
        {(ticket.installer_phone || ticket.installer_email) && (
          <Card className="p-4" data-testid="ticket-contact-card">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase text-slate-500">Contact</div>
              <div className="flex gap-1.5">
                {ticket.installer_phone && <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={() => setContactChannel('sms')} data-testid="ticket-sms-btn"><Send className="w-3.5 h-3.5 mr-1" /> Text</Button>}
                {ticket.installer_email && <Button size="sm" variant="outline" className="h-7 text-xs border-sky-300 text-sky-800 hover:bg-sky-50" onClick={() => setContactChannel('email')} data-testid="ticket-email-btn"><Mail className="w-3.5 h-3.5 mr-1" /> Email</Button>}
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {ticket.installer_phone && (
                <a
                  href={`tel:${ticket.installer_phone}`}
                  className="inline-flex items-center gap-2 text-sm text-slate-800 hover:text-slate-950 font-semibold"
                  data-testid="ticket-contact-phone"
                >
                  <Phone className="w-4 h-4 text-emerald-700" /> {ticket.installer_phone}
                </a>
              )}
              {ticket.installer_email && (
                <a
                  href={`mailto:${ticket.installer_email}`}
                  className="inline-flex items-center gap-2 text-sm text-slate-800 hover:text-slate-950 font-semibold"
                  data-testid="ticket-contact-email"
                >
                  <Mail className="w-4 h-4 text-sky-700" /> {ticket.installer_email}
                </a>
              )}
            </div>
          </Card>
        )}
        {/* Status + Priority header */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${pMeta.chip}`}>
                <PIcon className="w-3.5 h-3.5" /> {pMeta.label}
              </span>
              {ticket.category && (() => {
                const cat = CATEGORY_OPTIONS.find((c) => c.key === ticket.category);
                if (!cat) return null;
                const CIcon = cat.icon;
                return (
                  <span
                    className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-slate-900 text-white"
                    data-testid="ticket-category-chip"
                  >
                    <CIcon className="w-3.5 h-3.5" /> {cat.label}
                  </span>
                );
              })()}
            </div>
            <span className="text-xs text-slate-500">
              Raised {new Date(ticket.created_at).toLocaleString('en-GB')}
            </span>
          </div>
          {isStaff && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">Status</div>
                <div className="flex flex-wrap gap-1.5">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => !saving && patch({ status: s.key })}
                      disabled={saving}
                      data-testid={`ticket-status-${s.key}`}
                      className={`px-3 py-1 text-xs rounded-full border transition ${
                        ticket.status === s.key
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">Priority</div>
                <div className="flex flex-wrap gap-1.5">
                  {PRIORITY_OPTIONS.map((p) => {
                    const Icon = p.icon;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => !saving && patch({ priority: p.key })}
                        disabled={saving}
                        data-testid={`ticket-priority-set-${p.key}`}
                        className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full border transition ${
                          ticket.priority === p.key
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <Icon className="w-3 h-3" /> {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">Category</div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => !saving && patch({ category: '' })}
                    disabled={saving}
                    data-testid="ticket-category-clear"
                    className={`px-3 py-1 text-xs rounded-full border transition ${
                      !ticket.category
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    None
                  </button>
                  {CATEGORY_OPTIONS.map((c) => {
                    const Icon = c.icon;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => !saving && patch({ category: c.key })}
                        disabled={saving}
                        data-testid={`ticket-category-set-${c.key}`}
                        className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full border transition ${
                          ticket.category === c.key
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <Icon className="w-3 h-3" /> {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-[11px] font-bold uppercase text-slate-500 mb-1 flex items-center gap-1">
                  <User className="w-3 h-3" /> Assigned to
                </div>
                <select
                  value={ticket.assigned_to || ''}
                  onChange={(e) => !saving && patch({ assigned_to: e.target.value })}
                  disabled={saving}
                  data-testid="ticket-assign-select"
                  className="w-full sm:max-w-xs border rounded-md px-3 py-1.5 text-sm bg-white"
                >
                  <option value="">— Unassigned —</option>
                  {assignees.map((s) => (
                    <option key={s.user_id} value={s.user_id}>{s.name} ({s.role})</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </Card>

        {/* Description */}
        <Card className="p-4">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">Description</div>
          <div className="whitespace-pre-wrap text-sm text-slate-800">{ticket.description}</div>
        </Card>

        {/* Attachments — photos show as thumbnails; anyone who can see the ticket can add more */}
        <Card className="p-4" data-testid="ticket-attachments-card">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[11px] font-bold uppercase text-slate-500">Photos &amp; attachments ({(ticket.attachments || []).filter((a) => !a.note_id).length})</div>
            <AttachmentUploader ticketId={ticketId} onUploaded={(atts) => setTicket((t) => ({ ...t, attachments: [...(t.attachments || []), ...atts] }))} />
          </div>
          {(ticket.attachments || []).filter((a) => !a.note_id).length === 0 ? (
            <div className="text-sm text-slate-400">No photos or files yet — add one to show the issue{ticket.notes?.some((n) => n.attachments?.length) ? ' (images attached to notes are shown under each note)' : ''}.</div>
          ) : (
            <AttachmentGallery ticketId={ticketId} attachments={ticket.attachments.filter((a) => !a.note_id)} />
          )}
        </Card>

        {/* Notes — internal call notes. Staff-only, hidden from installers */}
        {isStaff && (
        <Card className="p-4" data-testid="ticket-notes-card">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-2 flex items-center gap-1.5">
            <StickyNote className="w-3.5 h-3.5" /> Notes ({ticket.notes?.length || 0})
            {(() => { const open = (ticket.notes || []).filter((n) => n.mentions?.length && !n.done_at).length; return open ? <span className="ml-2 text-[10px] font-bold text-white bg-[#7A0B7A] rounded-full px-1.5 py-0.5" data-testid="ticket-open-pings">{open} open ping{open === 1 ? '' : 's'}</span> : null; })()}
          </div>

          {(!ticket.notes || ticket.notes.length === 0) ? (
            <div className="text-sm text-slate-400 mb-3">No notes yet — add the first one below.</div>
          ) : (
            <ul className="space-y-2 mb-3">
              {ticket.notes.map((n) => {
                const roleLabel = ({ admin: 'Admin', manager: 'Manager', team: 'Team', installer: 'Installer' })[n.created_by_role] || n.created_by_role;
                const roleChip = ['admin', 'manager', 'team'].includes(n.created_by_role)
                  ? 'bg-slate-900 text-white'
                  : 'bg-emerald-100 text-emerald-800';
                return (
                  <li key={n.id} className={`border-l-2 pl-3 py-1 ${n.kind === 'sms' ? 'border-emerald-300 bg-emerald-50/40 rounded-r' : n.kind === 'email' ? 'border-sky-300 bg-sky-50/40 rounded-r' : 'border-slate-200'}`} data-testid={`ticket-note-${n.id}`}>
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-0.5 flex-wrap">
                      <span className="font-semibold text-slate-700">{n.created_by_name || 'Someone'}</span>
                      {n.kind && <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${n.kind === 'sms' ? 'bg-emerald-600 text-white' : 'bg-sky-600 text-white'}`} data-testid={`ticket-note-kind-${n.id}`}>{n.kind === 'sms' ? 'Text sent' : 'Email sent'} → {n.to}</span>}
                      <span className={`inline-block text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${roleChip}`}>
                        {roleLabel}
                      </span>
                      <span>{new Date(n.created_at).toLocaleString('en-GB')}</span>
                      {!n.kind && (
                        <button
                          type="button"
                          onClick={() => toggleDone(n)}
                          className={`ml-auto inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border ${n.done_at ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300 text-slate-500 hover:border-emerald-500 hover:text-emerald-700'}`}
                          data-testid={`ticket-note-done-${n.id}`}
                          title={n.done_at ? `Ticked done by ${n.done_by_name || 'staff'} · click to undo` : 'Tick as done / actioned'}
                        >
                          <Check className="w-3 h-3" /> {n.done_at ? 'Done' : 'Mark done'}
                        </button>
                      )}
                      {canAdmin && (
                        <button
                          type="button"
                          onClick={() => deleteNote(n.id)}
                          className={`text-slate-400 hover:text-rose-600 ${n.kind ? 'ml-auto' : ''}`}
                          data-testid={`ticket-note-delete-${n.id}`}
                          title="Delete note"
                          aria-label="Delete note"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {n.kind === 'email' && n.subject && <div className="text-xs font-semibold text-slate-700">{n.subject}</div>}
                    {n.body && <div className={`whitespace-pre-wrap text-sm ${n.done_at ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{renderWithMentions(n.body)}</div>}
                    {n.done_at && <div className="text-[10px] text-emerald-700 mt-0.5">Done · {n.done_by_name || 'staff'} · {new Date(n.done_at).toLocaleString('en-GB')}</div>}
                    {n.mentions?.length > 0 && <div className="text-[10px] text-slate-500 mt-0.5">Emailed {n.mentions.map((m) => m.name).join(', ')}</div>}
                    {n.attachments?.length > 0 && <div className="mt-1.5"><AttachmentGallery ticketId={ticketId} attachments={n.attachments} size="sm" testPrefix={`ticket-note-att`} /></div>}
                  </li>
                );
              })}
            </ul>
          )}

          <form onSubmit={addNote} className="flex flex-col gap-2">
            <NoteTemplateChips onPick={(t) => setNoteDraft((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t))} testid="ticket-note-template" />
            <MentionInput
              value={noteDraft}
              mentionIds={mentionIds}
              onChange={({ text, mentionIds: ids }) => { setNoteDraft(text); setMentionIds(ids); }}
              placeholder="Add a note — type @ to ping a colleague by email, e.g. '@Aimee can you call Steve?'"
              testid="ticket-note-input"
              disabled={addingNote}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <NoteImagePicker files={noteFiles} setFiles={setNoteFiles} />
              <Button
                type="submit"
                size="sm"
                disabled={addingNote || (!noteDraft.trim() && noteFiles.length === 0)}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="ticket-note-submit"
              >
                {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Add Note</>}
              </Button>
            </div>
          </form>
        </Card>
        )}
      </div>
      <SupportContactModal open={!!contactChannel} ticket={ticket} channel={contactChannel || 'sms'} staffName={user?.name} onClose={() => setContactChannel(null)} onSent={(note) => setTicket((t) => ({ ...t, notes: [...(t.notes || []), note], last_contact_channel: note.kind, last_contacted_at: note.created_at }))} />
    </div>
  );
}
